import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, CheckCircle, TrendingUp, Users, Zap, Globe, BarChart3, Phone, Mail, Clock, Target, Briefcase, Award, Database, Settings, Search, Smartphone, MessageSquare, Star } from 'lucide-react';
import { Header } from '@/layouts/Header';
import { Footer } from '@/layouts/Footer';
import { StickyCTA } from '@/components/StickyCTA';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from "@/features/shared/SEOHead";
import { trackCTAClick } from '@/lib/utils';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { generateProfessionalServiceSchema } from "@/lib/business-schema";


const ThunderBay = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What services does Frayze offer in Thunder Bay?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Frayze provides a full suite of digital growth services in Thunder Bay including website design and development, CRM system implementation (GoHighLevel, HubSpot), AI-powered business automation, lead generation systems, and local SEO. We serve trades, service businesses, automotive, and professional services across Northwestern Ontario."
        }
      },
      {
        "@type": "Question",
        "name": "How much does a website cost in Thunder Bay?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Website costs vary based on complexity. Our 7-Day Website Launch Program starts at $997 for a professional build, plus $189/month base with usage billed like a phone plan. Larger conversion-focused systems with full CRM automation are scoped to your business, with a fixed quote before you commit. All builds include mobile optimization, SEO foundations, and local Thunder Bay targeting."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer CRM systems for small businesses in Thunder Bay?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. We specialize in GoHighLevel (GHL) CRM implementations for Thunder Bay small businesses, trades, and service companies. Our CRM systems include automated lead follow-up, pipeline management, review generation, and reporting dashboards. We also implement HubSpot and Pipedrive for specific team needs."
        }
      },
      {
        "@type": "Question",
        "name": "How does AI automation help my Thunder Bay business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "AI automation eliminates repetitive manual tasks like lead follow-up, appointment reminders, data entry, invoice generation, and customer communication. Our Thunder Bay clients typically reduce manual processing time by 60-80%, often saving the equivalent of a full-time salary within the first year."
        }
      },
      {
        "@type": "Question",
        "name": "How long does it take to see results from SEO in Thunder Bay?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most Thunder Bay businesses start seeing movement in rankings and increased call volume within 60-90 days of implementation. SEO is a long-term investment, but the Thunder Bay market is less competitive than Toronto or Vancouver, meaning faster results are achievable with focused local optimization."
        }
      },
      {
        "@type": "Question",
        "name": "Is Frayze based in Thunder Bay?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Frayze Technologies Inc. is headquartered in Thunder Bay, Ontario. We are a 100% Canadian-owned digital agency that serves businesses across Northwestern Ontario, the GTA, Manitoba, and select North American markets. Our local presence means we understand the unique challenges and opportunities of doing business in Thunder Bay."
        }
      }
    ]
  };

  const businessSchema = generateProfessionalServiceSchema();


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SEOHead
        title="Thunder Bay Website Design & SEO Company | CRM & Leads | Frayze"
        description="Frayze is a Thunder Bay SEO and internet marketing company: websites, CRM, automation and local SEO that get Northern Ontario businesses found and booked."
        keywords="SEO company thunder bay, thunder bay SEO, thunder bay internet marketing, digital marketing thunder bay, northern ontario SEO, AI automation thunder bay, CRM thunder bay, website design thunder bay, lead generation thunder bay, digital agency thunder bay"
        ogTitle="Thunder Bay Website Design & SEO Company | CRM & Leads | Frayze"
        ogDescription="Frayze is a Thunder Bay SEO and internet marketing company: websites, CRM, automation and local SEO that get Northern Ontario businesses found and booked."
        canonical="https://frayze.ca/service-areas/thunder-bay/"
        robots="index, follow, max-snippet:-1, max-image-preview:large"
        breadcrumbs={[
          { name: "Home", item: "https://frayze.ca/" },
          { name: "Service Areas", item: "https://frayze.ca/service-areas/" },
          { name: "Thunder Bay", item: "https://frayze.ca/service-areas/thunder-bay/" }
        ]}
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            businessSchema,
            faqSchema
          ]
        }}
      />

      {/* Breadcrumb */}
      <div className="container mx-auto px-6 py-4 absolute top-20 z-20">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/service-areas/">Service Areas</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Thunder Bay</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <main>
        {/* ===== HERO SECTION ===== */}
        <section className="relative overflow-hidden pt-40 md:pt-48 pb-20">
          <div
            className="absolute top-0 left-0 w-full h-[800px] pointer-events-none overflow-hidden z-0"
            style={{
              maskImage: 'radial-gradient(ellipse 72% 58% at 50% 40%, black 0%, black 25%, transparent 70%)',
              WebkitMaskImage: 'radial-gradient(ellipse 72% 58% at 50% 40%, black 0%, black 25%, transparent 70%)'
            }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_18%,rgba(0,102,255,0.35),transparent_55%),radial-gradient(circle_at_82%_12%,rgba(127,0,255,0.28),transparent_60%),radial-gradient(circle_at_50%_82%,rgba(0,204,255,0.18),transparent_65%)] blur-3xl" />
          </div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="outline" className="mb-6 border-primary/20 bg-primary/5 text-primary backdrop-blur-sm">
                <MapPin className="h-3 w-3 mr-1.5" />
                Thunder Bay, Ontario. Our HQ
              </Badge>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
                Website Design, SEO, AI & Marketing{" "}
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">

                  in Thunder Bay
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-muted-foreground mb-10 leading-relaxed max-w-3xl mx-auto">
                Frayze is the Thunder Bay SEO and digital marketing company building the connected
                digital infrastructure that helps Northern Ontario businesses get found, capture more
                leads, and close faster. Websites, local SEO, CRM, and automation live in 7 days.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                <Button
                  size="lg"
                  className="h-14 px-8 text-lg font-bold shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all duration-300 group"
                  asChild
                >
                  <a
                    href="/website-launch-program/"
                    onClick={() => trackCTAClick({ cta_text: 'See How It Works', cta_location: 'thunder_bay_hero', cta_type: 'launch_cta', cta_url: '/website-launch-program/' })}
                  >
                    Launch My Website in 7 Days
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="h-14 px-8 text-lg hover:bg-muted/50 bg-background/50 backdrop-blur-sm"
                  asChild
                >
                  <a href="/digital-audit/">Get Your Automation Score</a>
                </Button>
              </div>
            </div>

            {/* ===== PROOF STRIP ===== */}
            <div className="mt-16 mx-auto max-w-5xl">
              <div className="rounded-2xl border border-border/60 bg-muted/40 backdrop-blur-sm px-6 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                  <div className="text-center md:text-left">
                    <div className="text-primary font-bold text-sm mb-1 uppercase tracking-wider">Founder Track Record</div>
                    <div className="text-xl font-bold">Before Frayze: 6-Rooftop GTA Dealer Group</div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                    <div>
                      <div className="text-3xl font-bold text-primary">47%</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">Sales Volume Growth</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-primary">52%</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">Lower Tech Costs</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-primary">400%</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">Lead Increase</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-primary">$1.7M</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">Saved Annually</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== WHY THUNDER BAY NEEDS A GROWTH SYSTEM ===== */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Thunder Bay Businesses Need a Connected Growth System</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Thunder Bay is a city of 110,000+ with a strong foundation in trades, healthcare, education, and
                tourism. But most local businesses are still running on disconnected tools: a website here, a
                spreadsheet there, and manual follow-up everywhere. That costs you leads, time, and money.
              </p>
            </div>

            <div className="prose prose-lg max-w-none text-muted-foreground mb-12">
              <p>
                At Frayze, we believe every Thunder Bay business, whether you're a plumber on the North Side,
                a med spa on Memorial Avenue, or a growing auto dealer on Arthur Street, deserves the same
                enterprise-grade infrastructure that large corporations use. The difference? We build it to fit your
                budget and your local market.
              </p>
              <p>
                Our approach is simple: we unify your website, CRM, automation, lead generation, and SEO into one
                connected system. When a customer searches "plumber near me" in Thunder Bay, your website
                captures the lead, your CRM logs it, your automation sends instant follow-up, and your dashboard
                tracks the ROI, all without you lifting a finger.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-red-200 bg-red-50/50">
                <CardContent className="pt-6">
                  <div className="text-red-600 font-bold mb-2">The Problem</div>
                  <p className="text-sm text-muted-foreground">
                    Disconnected tools, manual follow-up, leads falling through the cracks, and no visibility into what's actually working.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-yellow-200 bg-yellow-50/50">
                <CardContent className="pt-6">
                  <div className="text-yellow-700 font-bold mb-2">The Cost</div>
                  <p className="text-sm text-muted-foreground">
                    The average small business loses 30-50% of its leads due to slow follow-up. In Thunder Bay's competitive service market, that's thousands in lost revenue monthly.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50/50">
                <CardContent className="pt-6">
                  <div className="text-green-700 font-bold mb-2">The Solution</div>
                  <p className="text-sm text-muted-foreground">
                    A unified growth system from Frayze that captures, nurtures, and converts leads automatically, while giving you a single dashboard to manage it all.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* ===== WEBSITE DEVELOPMENT SECTION ===== */}
        <section className="py-24 bg-muted/30" id="web-design">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Globe className="h-6 w-6" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">Thunder Bay Website Design</h2>
            </div>
            <p className="text-lg text-muted-foreground mb-10 max-w-3xl">
              Your website is the foundation of your entire digital presence. We build high-performance,
              mobile-first websites for Thunder Bay businesses that don't just look professional. They
              convert visitors into paying customers.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <Smartphone className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Mobile-First, Responsive Design</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Over 70% of Thunder Bay searches happen on mobile. Every site we build is optimized for phones, tablets, and desktops. <strong>Result: frictionless user experience.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <Search className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">SEO-Ready Architecture</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Built with local Thunder Bay SEO baked in: proper heading structure, schema markup, fast page speeds, and optimized meta data. <strong>Result: rank higher from day one.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <Database className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">CRM & Booking Integration</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Your website connects directly to your CRM. Leads flow into your pipeline, appointments get booked, and nothing falls through the cracks. <strong>Result: zero missed opportunities.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <Zap className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Lightning Fast Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Fast, reliable hosting and modern build tools deliver sub-second load times. 53% of visitors leave sites that take longer than 3 seconds. <strong>Result: lower bounce rates, more leads.</strong></p>
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-wrap gap-4">
              <Button variant="default" asChild>
                <Link to="/solutions/websites/">
                  Explore Web Design Services <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/website-launch-program/">7-Day Launch Program: Starting at $997</Link>
              </Button>
              <Button variant="ghost" asChild className="text-muted-foreground">
                <Link to="/solutions/websites/">Learn about our Web Design & SEO system <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </section>

        {/* ===== CRM SYSTEMS SECTION ===== */}
        <section className="py-24 bg-background" id="crm-systems">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Users className="h-6 w-6" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">CRM Systems</h2>
            </div>
            <p className="text-lg text-muted-foreground mb-10 max-w-3xl">
              Stop running your business on sticky notes and spreadsheets. We implement CRM systems
              purpose-built for Thunder Bay trades, service businesses, and professional services, so
              every lead, every job, and every follow-up is tracked in one place.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <Database className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Centralized Lead Hub</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Every lead from every source, website, Google, social, referral, in one dashboard. No more checking five different apps. <strong>Result: higher close rates.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <TrendingUp className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Custom Sales Pipelines</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Track jobs from quote to completion with pipelines designed for your workflow, whether you're an HVAC company or a dental office. <strong>Result: predictable revenue.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <Star className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Automated Review Generation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Automatically request 5-star Google reviews after every completed job. Your reputation builds itself. <strong>Result: dominant local reputation.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <BarChart3 className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Real-Time Reporting</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Know exactly where your revenue is coming from with dashboards that update in real time. No guesswork. <strong>Result: confident decision making.</strong></p>
                </CardContent>
              </Card>
            </div>

            <Button variant="default" asChild>
              <Link to="/solutions/crm-automation/">
                Explore CRM Systems <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="ghost" asChild className="text-muted-foreground ml-2">
              <Link to="/solutions/crm-automation/">Learn about CRM Automation <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </section>

        {/* ===== AI AUTOMATION SECTION ===== */}
        <section className="py-24 bg-muted/30" id="ai-automation">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Zap className="h-6 w-6" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">AI Automation</h2>
            </div>
            <p className="text-lg text-muted-foreground mb-10 max-w-3xl">
              The average Thunder Bay business owner spends 15+ hours per week on tasks that could be
              automated: data entry, follow-up emails, appointment reminders, invoice generation. We
              eliminate the grunt work so you can focus on what actually grows revenue.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <MessageSquare className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Lead & Sales Automation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Instantly capture and follow up with every lead via SMS, email, and voicemail drops. Speed-to-lead is the #1 factor in conversion. <strong>Result: zero missed opportunities.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <Settings className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Automated Workflows</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Connect 5,000+ apps with tools like Zapier, Make.com, and custom API integrations. Automate complex multi-step processes across your entire stack. <strong>Result: fewer errors, faster delivery.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <Clock className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Customer Communication</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Automated appointment reminders, follow-up sequences, birthday messages, and satisfaction surveys. Keep customers engaged without manual effort. <strong>Result: higher retention.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <BarChart3 className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Automated Reporting</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Real-time dashboards that show you exactly where your money is going and where it's coming from. No more manual spreadsheet reports. <strong>Result: data-driven growth.</strong></p>
                </CardContent>
              </Card>
            </div>

            <Button variant="default" asChild>
              <Link to="/solutions/crm-automation/">
                Explore AI Automation <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="ghost" asChild className="text-muted-foreground ml-2">
              <Link to="/solutions/ai-strategy-rollout/">Learn about Enterprise AI <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </section>

        {/* ===== LEAD GENERATION SECTION ===== */}
        <section className="py-24 bg-background" id="lead-generation">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Target className="h-6 w-6" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">Lead Generation</h2>
            </div>
            <p className="text-lg text-muted-foreground mb-10 max-w-3xl">
              More calls, more quotes, more revenue. We don't just "get leads": we build the automated
              engine that fills your calendar with qualified Thunder Bay customers who are ready to buy.
              Our systems run 24/7, capturing demand from Google, Meta, and organic search.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <Target className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Lead Magnets & Offers</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Custom calculators, guides, and promotions that stop the scroll and capture contact info from high-intent Thunder Bay prospects. <strong>Result: lower cost per lead.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <Zap className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Instant AI Follow-Up</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">New leads receive an SMS within seconds. Speed-to-lead is the single largest predictor of conversion. We respond while your competitors sleep. <strong>Result: 4x higher close rate.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <Globe className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Multi-Channel Campaigns</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Google Ads, Facebook, Instagram, and SEO all feeding into a single unified inbox. See every lead from every channel in one place. <strong>Result: complete pipeline visibility.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <BarChart3 className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Cost-Per-Lead Tracking</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Know exactly what each customer costs to acquire. Our dashboards connect ad spend to closed deals so you can scale with confidence. <strong>Result: predictable growth.</strong></p>
                </CardContent>
              </Card>
            </div>

            <Button variant="default" asChild>
              <Link to="/solutions/lead-generation-system/">
                Explore Lead Generation <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="ghost" asChild className="text-muted-foreground ml-2">
              <Link to="/solutions/lead-generation-system/">Learn about Lead Gen Systems <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </section>

        {/* ===== LOCAL SEO SECTION ===== */}
        <section className="py-24 bg-muted/30" id="local-seo">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Search className="h-6 w-6" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">Local SEO</h2>
            </div>
            <p className="text-lg text-muted-foreground mb-10 max-w-3xl">
              When someone searches "plumber near me" or "best HVAC Thunder Bay," your business needs to
              appear at the top. We build <strong>Thunder Bay SEO systems</strong> that dominate both
              Google Maps and organic results, so your phone rings instead of your competitor's.
            </p>
            <p className="text-muted-foreground mb-10 max-w-3xl">
              Comparing providers? Read our straight-answer guide:{" "}
              <a href="/blog/seo-company-thunder-bay-what-to-expect" className="text-primary underline underline-offset-4 hover:text-primary/80">
                choosing an SEO company in Thunder Bay, with real costs and timelines
              </a>.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <MapPin className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Google Business Profile Optimization</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">We optimize your GBP listing for Thunder Bay keywords, manage reviews, and ensure your NAP consistency across 80+ directories. <strong>Result: dominant map pack visibility.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <TrendingUp className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">On-Page & Technical SEO</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Proper heading structure, schema markup, fast page speeds, and locally-optimized content built around the keywords Thunder Bay customers actually search. <strong>Result: higher organic rankings.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <BarChart3 className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Rank Tracking & Reporting</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Monthly reports showing your keyword positions, organic traffic, and call volume from search. Real data, not vanity metrics. <strong>Result: measurable ROI.</strong></p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow border-primary/5">
                <CardHeader>
                  <Globe className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Content & Authority Building</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Local landing pages, service-area content, and citation building that position you as the authority in your Thunder Bay niche. <strong>Result: compounding organic traffic.</strong></p>
                </CardContent>
              </Card>
            </div>

            <Button variant="default" asChild>
              <Link to="/solutions/websites/">
                Explore Thunder Bay SEO Services <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>

        {/* ===== HOW WE WORK . PROCESS ===== */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-6 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">How We Build Your Growth System</h2>
            <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
              Every Thunder Bay engagement follows our proven 4-step process. We start with data, build with precision, and deliver measurable results.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="space-y-3 text-center">
                <div className="text-5xl font-bold text-primary/70">01</div>
                <div className="font-bold text-lg">Discovery & Audit</div>
                <p className="text-sm text-muted-foreground">We map your current tools, identify bottlenecks, and analyze your competitive position in the Thunder Bay market.</p>
              </div>
              <div className="space-y-3 text-center">
                <div className="text-5xl font-bold text-primary/70">02</div>
                <div className="font-bold text-lg">Design & Build</div>
                <p className="text-sm text-muted-foreground">We design your custom growth system, website, CRM, automation, and campaigns, in a staging environment before going live.</p>
              </div>
              <div className="space-y-3 text-center">
                <div className="text-5xl font-bold text-primary/70">03</div>
                <div className="font-bold text-lg">Launch & Optimize</div>
                <p className="text-sm text-muted-foreground">Go-live with a full optimization blitz. We monitor performance, run A/B tests, and refine until your system hits peak efficiency.</p>
              </div>
              <div className="space-y-3 text-center">
                <div className="text-5xl font-bold text-primary/70">04</div>
                <div className="font-bold text-lg">Support & Scale</div>
                <p className="text-sm text-muted-foreground">Ongoing protection, updates, and data-driven improvements. As you grow, your system grows with you.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== INDUSTRIES WE SERVE ===== */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-6 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Industries We Serve in Thunder Bay</h2>
            <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
              Our growth systems are designed for service-based businesses that depend on local customers and repeat business. If you have customers, our system works.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                { name: "Trades & Contractors", href: "/industries/trades/" },
                { name: "Automotive & Marine", href: "/automotive/" },
                { name: "Med Spas & Wellness", href: "/solutions/" },
                { name: "Dental Clinics", href: "/solutions/" },
                { name: "Chiropractic", href: "/solutions/" },
                { name: "HVAC Companies", href: "/industries/trades/" },
                { name: "Cleaning Services", href: "/solutions/" },
                { name: "Lawn Care", href: "/solutions/" },
              ].map((industry) => (
                <Link
                  key={industry.name}
                  to={industry.href}
                  className="flex items-center gap-2 p-4 rounded-xl border hover:border-primary/30 hover:bg-primary/5 transition-all group"
                >
                  <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-sm font-medium group-hover:text-primary transition-colors">{industry.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ===== AREAS WE SERVE . LOCAL COVERAGE ===== */}
        <section className="py-24 bg-background border-t">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Serving Thunder Bay &amp; Northwestern Ontario</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                We&rsquo;re based here. From the waterfront to the outlying townships, we build and run
                growth systems for businesses across the region, remotely managed, locally focused.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="rounded-2xl border bg-muted/20 p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" /> Across Thunder Bay
                </h3>
                <div className="flex flex-wrap gap-2">
                  {["Port Arthur", "Fort William", "Westfort", "Current River", "Northwood", "County Park", "Intercity"].map((area) => (
                    <span key={area} className="inline-flex items-center rounded-full border bg-background px-3 py-1.5 text-sm font-medium">
                      {area}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border bg-muted/20 p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-primary" /> Surrounding Communities
                </h3>
                <div className="flex flex-wrap gap-2">
                  {["Kakabeka Falls", "Oliver Paipoonge", "Shuniah", "Neebing", "Murillo", "Nipigon", "Kaministiquia"].map((area) => (
                    <span key={area} className="inline-flex items-center rounded-full border bg-background px-3 py-1.5 text-sm font-medium">
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <p className="text-center text-sm text-muted-foreground mt-8 max-w-2xl mx-auto">
              Serving businesses across Northwestern Ontario, and remotely for clients throughout
              Ontario and Canada.
            </p>
          </div>
        </section>

        {/* ===== CORE GROWTH SYSTEMS . INTERNAL LINKING HUB ===== */}
        <section className="py-20 bg-muted/30 border-y">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-3">Our Core Growth Systems</h2>
              <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
                Every Thunder Bay engagement is powered by our proven systems. Each module works independently, but they're designed to work together.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Link to="/solutions/websites/" className="group p-6 bg-background rounded-2xl border hover:border-primary/30 hover:shadow-lg transition-all">
                <Globe className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Web Design & Local SEO</h3>
                <p className="text-sm text-muted-foreground">High-performance websites built for search visibility and lead conversion in Thunder Bay and Northern Ontario.</p>
                <span className="text-sm font-bold text-primary mt-3 inline-flex items-center gap-1">Explore system <ArrowRight className="w-3 h-3" /></span>
              </Link>
              <Link to="/solutions/crm-automation/" className="group p-6 bg-background rounded-2xl border hover:border-primary/30 hover:shadow-lg transition-all">
                <Users className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">CRM Automation</h3>
                <p className="text-sm text-muted-foreground">Centralized lead management with automated follow-up, pipeline tracking, and review generation.</p>
                <span className="text-sm font-bold text-primary mt-3 inline-flex items-center gap-1">Explore system <ArrowRight className="w-3 h-3" /></span>
              </Link>
              <Link to="/solutions/lead-generation-system/" className="group p-6 bg-background rounded-2xl border hover:border-primary/30 hover:shadow-lg transition-all">
                <Target className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Lead Generation</h3>
                <p className="text-sm text-muted-foreground">Multi-channel lead capture and AI-powered instant follow-up that fills your calendar with qualified Thunder Bay customers.</p>
                <span className="text-sm font-bold text-primary mt-3 inline-flex items-center gap-1">Explore system <ArrowRight className="w-3 h-3" /></span>
              </Link>
              <Link to="/solutions/ai-strategy-rollout/" className="group p-6 bg-background rounded-2xl border hover:border-primary/30 hover:shadow-lg transition-all">
                <Zap className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Enterprise AI</h3>
                <p className="text-sm text-muted-foreground">Voice AI, automated workflows, and custom automation that replaces manual grunt work across your entire operation.</p>
                <span className="text-sm font-bold text-primary mt-3 inline-flex items-center gap-1">Explore system <ArrowRight className="w-3 h-3" /></span>
              </Link>
              <Link to="/solutions/ad-management-scale/" className="group p-6 bg-background rounded-2xl border hover:border-primary/30 hover:shadow-lg transition-all">
                <TrendingUp className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Ad Management & Scale</h3>
                <p className="text-sm text-muted-foreground">Google & Meta ads with full ROI visibility, optimized for Northern Ontario high-ticket trades and service businesses.</p>
                <span className="text-sm font-bold text-primary mt-3 inline-flex items-center gap-1">Explore system <ArrowRight className="w-3 h-3" /></span>
              </Link>
              <Link to="/website-launch-program/" className="group p-6 bg-primary/5 rounded-2xl border border-primary/20 hover:border-primary/40 hover:shadow-lg transition-all">
                <Briefcase className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">7-Day Website Launch</h3>
                <p className="text-sm text-muted-foreground">The fastest path to a professional website + AI business system. Starting at $997.</p>
                <span className="text-sm font-bold text-primary mt-3 inline-flex items-center gap-1">Get started <ArrowRight className="w-3 h-3" /></span>
              </Link>
            </div>
          </div>
        </section>

        {/* ===== LOCAL CASE STUDY PROOF ===== */}
        <section className="py-20 bg-muted/30 border-y">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-3">Built on Systems Proven at Scale</h2>
              <p className="text-muted-foreground text-lg">The track record behind Frayze: before we ever opened our doors.</p>
            </div>
            <Link to="/case-studies/need-a-car-automotive-growth/" className="group flex flex-col md:flex-row items-center gap-8 bg-background border rounded-2xl p-8 md:p-12 hover:border-primary/40 hover:shadow-lg transition-all">
              <div className="flex-1">
                <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Founder Track Record • Automotive</Badge>
                <h3 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">Need A Car: Founder-Built Growth System</h3>
                <p className="text-muted-foreground text-lg mb-4">Before Frayze, our founder built and ran the CRM, AI, and lead-generation system that delivered a 400% lead increase and $1.7M in annual marketing savings for a 6-location automotive group. Frayze is those same systems.</p>
                <span className="text-primary font-bold inline-flex items-center gap-1">Read the full case study <ArrowRight className="w-4 h-4" /></span>
              </div>
              <div className="w-full md:w-48 shrink-0">
                <div className="bg-primary/5 rounded-2xl p-6 text-center">
                  <div className="text-5xl font-bold text-primary">400%</div>
                  <div className="text-sm font-medium text-muted-foreground mt-1">Lead Increase</div>
                  <div className="text-xs text-muted-foreground mt-1">+ $1.7M saved</div>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* ===== FAQ SECTION ===== */}
        <section className="py-24 bg-muted/20">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full space-y-4">
              <AccordionItem value="item-1" className="border rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold">What services does Frayze offer in Thunder Bay?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Frayze provides a full suite of digital growth services in Thunder Bay including website design and development, CRM system implementation (GoHighLevel, HubSpot), AI-powered business automation, lead generation systems, and local SEO. We serve trades, service businesses, automotive, and professional services across Northwestern Ontario.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold">How much does a website cost in Thunder Bay?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Website costs vary based on complexity. Our 7-Day Website Launch Program starts at $997 for a professional build, plus $189/month base with usage billed like a phone plan. Larger conversion-focused systems with full CRM automation are scoped to your business, with a fixed quote before you commit. All builds include mobile optimization, SEO foundations, and local Thunder Bay targeting.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold">Do you offer CRM systems for small businesses in Thunder Bay?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Yes. We specialize in GoHighLevel (GHL) CRM implementations for Thunder Bay small businesses, trades, and service companies. Our CRM systems include automated lead follow-up, pipeline management, review generation, and reporting dashboards. We also implement HubSpot and Pipedrive for specific team needs.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4" className="border rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold">How does AI automation help my Thunder Bay business?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  AI automation eliminates repetitive manual tasks like lead follow-up, appointment reminders, data entry, invoice generation, and customer communication. Our Thunder Bay clients typically reduce manual processing time by 60-80%, often saving the equivalent of a full-time salary within the first year.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-5" className="border rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold">How long does it take to see results from SEO in Thunder Bay?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Most Thunder Bay businesses start seeing movement in rankings and increased call volume within 60-90 days of implementation. SEO is a long-term investment, but the Thunder Bay market is less competitive than Toronto or Vancouver, meaning faster results are achievable with focused local optimization.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-6" className="border rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold">Is Frayze based in Thunder Bay?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Yes. Frayze Technologies Inc. is headquartered in Thunder Bay, Ontario. We are a 100% Canadian-owned digital agency that serves businesses across Northwestern Ontario, the GTA, Manitoba, and select North American markets. Our local presence means we understand the unique challenges and opportunities of doing business in Thunder Bay.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* ===== FINAL CTA ===== */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="rounded-2xl border border-primary/20 bg-primary/5 text-center p-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Build Your Thunder Bay Growth Engine?</h2>
                <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
                  Book a free strategy session and see exactly how to get found by more Thunder Bay
                  customers and turn them into booked jobs. No fluff: just a clear, practical plan.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="lg"
                    className="h-14 px-10 text-lg font-bold shadow-lg shadow-primary/25"
                    asChild
                  >
                    <a
                      href="/book-consultation/"
                      onClick={() => trackCTAClick({ cta_text: 'Book Strategy Call', cta_location: 'thunder_bay_final_cta', cta_type: 'consultation_cta', cta_url: '/book-consultation/' })}
                    >
                      Book My Free Strategy Call
                    </a>
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-14 px-10 text-lg font-bold"
                    asChild
                  >
                    <a href="tel:+18077000079">Call (807) 700-0079</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <StickyCTA />
    </div>
  );
};

export default ThunderBay;
